/**
 * Identifier schemas.
 *
 * These are the validating constructors `shared-types/brand.ts` points at. Everywhere else casts
 * with `unsafeBrand`, which is deliberately named to be greppable; this is where an id coming
 * from outside the process actually gets checked.
 *
 * The format is deliberately permissive: ids are opaque, and the system must accept ids minted
 * by an older client, by an import, or by a server that changed its generator. What it must
 * reject is the shape that breaks something downstream — empty, whitespace, absurdly long, or
 * carrying characters that would need escaping in a storage key or a URL segment.
 */
import * as z from 'zod';
/** Upper bound on any identifier. Generous, but bounded: an unbounded id is a storage attack. */
export const MAX_ID_LENGTH = 128;
/**
 * Letters, digits, hyphen and underscore.
 *
 * Chosen so an id is safe unescaped in an R2 object key, a URL path segment and a filename at
 * the same time. Anything else has to be encoded somewhere, and the encoding is always forgotten
 * in one of the three.
 */
const ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const identifier = z
    .string()
    .min(1, 'identifier must not be empty')
    .max(MAX_ID_LENGTH, `identifier must be at most ${String(MAX_ID_LENGTH)} characters`)
    .regex(ID_PATTERN, 'identifier may contain only letters, digits, hyphen and underscore');
function idSchema() {
    return identifier.transform((value) => value);
}
export const userIdSchema = idSchema();
export const workspaceIdSchema = idSchema();
export const projectIdSchema = idSchema();
export const folderIdSchema = idSchema();
export const mapIdSchema = idSchema();
export const nodeIdSchema = idSchema();
export const edgeIdSchema = idSchema();
export const attachmentIdSchema = idSchema();
export const tagIdSchema = idSchema();
export const themeIdSchema = idSchema();
export const presentationIdSchema = idSchema();
export const presentationStepIdSchema = idSchema();
export const operationIdSchema = idSchema();
export const correlationIdSchema = idSchema();
export const idempotencyKeySchema = idSchema();
/**
 * An R2 object key.
 *
 * Not an identifier: keys are paths, so slashes are allowed. What is rejected is anything that
 * could escape the intended prefix — a leading slash, a `..` segment, a backslash — because a
 * key is what enforces workspace isolation in the bucket.
 */
export const storageKeySchema = z
    .string()
    .min(1)
    .max(1024)
    .refine((value) => !value.startsWith('/'), 'storage key must be relative')
    .refine((value) => !value.includes(String.fromCharCode(92)), 'no backslashes in a storage key')
    .refine((value) => !value.split('/').includes('..'), 'storage key must not contain a parent-directory segment')
    .transform((value) => value);
//# sourceMappingURL=ids.js.map